import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface AvailabilityBlock {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  specificDate: string | null;
}

interface BlockedDate {
  id: string;
  date: string;
  reason: string | null;
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function Availability() {
  const [availability, setAvailability] = useState<AvailabilityBlock[]>([]);
  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [formDayOfWeek, setFormDayOfWeek] = useState(1);
  const [formStartTime, setFormStartTime] = useState('09:00');
  const [formEndTime, setFormEndTime] = useState('17:00');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Blocked date form
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockDate, setBlockDate] = useState('');
  const [blockReason, setBlockReason] = useState('');

  const fetchData = async () => {
    try {
      const response = await api.get('/availability');
      setAvailability(response.data.availability);
      setBlockedDates(response.data.blockedDates);
    } catch {
      setError('Failed to load availability');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddAvailability = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/availability', {
        dayOfWeek: formDayOfWeek,
        startTime: formStartTime,
        endTime: formEndTime
      });
      await fetchData();
      setShowAddForm(false);
      setFormStartTime('09:00');
      setFormEndTime('17:00');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to add availability');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteAvailability = async (id: string) => {
    if (!confirm('Delete this availability block?')) return;

    try {
      await api.delete(`/availability/${id}`);
      await fetchData();
    } catch {
      setError('Failed to delete availability');
    }
  };

  const handleAddBlockedDate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await api.post('/availability/blocked', {
        date: blockDate,
        reason: blockReason || undefined
      });
      await fetchData();
      setShowBlockForm(false);
      setBlockDate('');
      setBlockReason('');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to block date');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteBlockedDate = async (id: string) => {
    try {
      await api.delete(`/availability/blocked/${id}`);
      await fetchData();
    } catch {
      setError('Failed to remove blocked date');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Group availability by day
  const availabilityByDay = DAYS.map((day, index) => ({
    day,
    blocks: availability.filter((a) => a.dayOfWeek === index && a.isRecurring)
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="page-title mb-0">Availability</h1>
        <button onClick={() => setShowAddForm(true)} className="btn-primary">
          Add Availability
        </button>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Add Availability Form */}
      {showAddForm && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Add Availability</h2>
          <form onSubmit={handleAddAvailability} className="space-y-4">
            <div>
              <label className="label">Day of Week</label>
              <select
                value={formDayOfWeek}
                onChange={(e) => setFormDayOfWeek(parseInt(e.target.value))}
                className="input"
              >
                {DAYS.map((day, index) => (
                  <option key={index} value={index}>{day}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Start Time</label>
                <input
                  type="time"
                  value={formStartTime}
                  onChange={(e) => setFormStartTime(e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">End Time</label>
                <input
                  type="time"
                  value={formEndTime}
                  onChange={(e) => setFormEndTime(e.target.value)}
                  className="input"
                  required
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Weekly Availability */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Weekly Schedule</h2>
        <div className="space-y-4">
          {availabilityByDay.map(({ day, blocks }) => (
            <div key={day} className="flex items-start gap-4">
              <div className="w-24 font-medium text-gray-300">{day}</div>
              <div className="flex-1">
                {blocks.length === 0 ? (
                  <span className="text-gray-500">Not available</span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {blocks.map((block) => (
                      <span
                        key={block.id}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-[#1111d4]/20 text-[#1111d4] rounded-full text-sm border border-[#1111d4]/30"
                      >
                        {block.startTime.slice(0, 5)} - {block.endTime.slice(0, 5)}
                        <button
                          onClick={() => handleDeleteAvailability(block.id)}
                          className="text-[#1111d4] hover:text-white"
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Blocked Dates */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Blocked Dates</h2>
          <button onClick={() => setShowBlockForm(true)} className="btn-secondary text-sm">
            Block a Date
          </button>
        </div>

        {showBlockForm && (
          <form onSubmit={handleAddBlockedDate} className="mb-4 p-4 bg-[#252550] rounded-lg space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Date</label>
                <input
                  type="date"
                  value={blockDate}
                  onChange={(e) => setBlockDate(e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Reason (optional)</label>
                <input
                  type="text"
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  className="input"
                  placeholder="e.g., Holiday"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={isSubmitting} className="btn-primary text-sm">
                Block Date
              </button>
              <button
                type="button"
                onClick={() => setShowBlockForm(false)}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {blockedDates.length === 0 ? (
          <p className="text-gray-500">No blocked dates</p>
        ) : (
          <div className="space-y-2">
            {blockedDates.map((blocked) => (
              <div
                key={blocked.id}
                className="flex items-center justify-between p-3 bg-red-900/20 border border-red-800/30 rounded-lg"
              >
                <div>
                  <span className="font-medium text-red-300">
                    {format(new Date(blocked.date), 'EEEE, MMMM d, yyyy')}
                  </span>
                  {blocked.reason && (
                    <span className="text-gray-400 ml-2">- {blocked.reason}</span>
                  )}
                </div>
                <button
                  onClick={() => handleDeleteBlockedDate(blocked.id)}
                  className="text-red-400 hover:text-red-300 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
